import { OppButton } from '../components/ui/control';
import { useState, useMemo, useRef, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useSharedAuth } from '@litopia/shared';
import {
  Compass, Globe, BookOpen, Bookmark,
  Heart, Eye, Star, PenLine, Feather,
  ChevronLeft, ChevronRight, SlidersHorizontal,
  X, Droplets,
} from 'lucide-react';
import { useCopy } from '../copy';
import {
  usePublicLetters, useToggleLike, useMyLikedLetterIds,
  useMuseumItems, useMuseumExhibitions, useExhibitionEntries,
  useMyCollections, useToggleCollection,
} from '../hooks/useOpp';
import { GridItemSkeleton, LetterCardSkeleton } from '../components/ui/Skeleton';
import CommentSection from '../components/CommentSection';
import { SemanticState } from '../components/semantic/SemanticState';

type DiscoverTab = 'plaza' | 'museum' | 'drift';

export default function DiscoverPage() {
  const { t } = useCopy();
  const [searchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') as DiscoverTab) || 'plaza';
  const [activeTab, setActiveTab] = useState<DiscoverTab>(initialTab);

  const TABS: { id: DiscoverTab; label: string; icon: React.ElementType }[] = [
    { id: 'plaza', label: t('discover.tab.plaza'), icon: Globe },
    { id: 'museum', label: t('discover.tab.museum'), icon: BookOpen },
    { id: 'drift', label: t('discover.tab.drift'), icon: Droplets },
  ];

  return (
    <div className="space-y-5 pb-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-2xl font-bold text-[hsl(var(--opp-ink))]">{t('nav.discover')}</h1>
          <p className="text-sm text-[hsl(var(--opp-accent-strong))]">{t('discover.subtitle')}</p>
        </div>
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[hsl(var(--opp-accent-soft))]">
          <Compass className="h-5 w-5 text-[hsl(var(--opp-accent-strong))]" />
        </div>
      </div>

      {/* Tab bar */}
      <div role="tablist" className="flex gap-1 rounded-xl bg-[hsl(var(--opp-accent-soft))] p-1">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <OppButton
              key={tab.id}
              role="tab"
              aria-selected={isActive}
              aria-controls={`tabpanel-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-all ${
                isActive
                  ? 'bg-[hsl(var(--opp-surface))] text-[hsl(var(--opp-ink))] shadow-sm'
                  : 'text-[hsl(var(--opp-accent))] hover:text-[hsl(var(--opp-accent-strong))]'
              }`}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
            </OppButton>
          );
        })}
      </div>

      {/* Tab content */}
      <div role="tabpanel" id={`tabpanel-${activeTab}`}>
        {activeTab === 'plaza' && <PlazaTab />}
        {activeTab === 'museum' && <MuseumTab />}
        {activeTab === 'drift' && <DriftTab />}
      </div>
    </div>
  );
}

// ── Plaza Tab (from Plaza.tsx) ──
function PlazaTab() {
  const { t } = useCopy();
  const { user } = useSharedAuth();
  const { data: letters, isLoading } = usePublicLetters({ limit: 20 });
  const { data: likedIds } = useMyLikedLetterIds();
  const toggleLikeMutation = useToggleLike();
  const [filter, setFilter] = useState('all');

  const FILTERS: { id: string; label: string }[] = [
    { id: 'all', label: t('plaza.filter.all') },
    { id: 'trending', label: t('plaza.filter.trending') },
    { id: 'recent', label: t('plaza.filter.recent') },
    { id: 'heartfelt', label: t('plaza.filter.heartfelt') },
    { id: 'future', label: t('plaza.filter.future') },
  ];

  const filteredLetters = useMemo(() => {
    if (!letters) return [];
    switch (filter) {
      case 'trending':
        return [...letters].sort((a, b) => (b.like_count ?? 0) - (a.like_count ?? 0));
      case 'recent':
        // Database already sorted by created_at DESC, no need to re-sort
        return letters;
      case 'heartfelt':
        return [...letters].filter((l) => (l.like_count ?? 0) >= 3);
      case 'future':
        return letters.filter((l) => l.type === 'future');
      default:
        return letters;
    }
  }, [letters, filter]);

  const toggleLike = (id: string) => {
    if (!user) return;
    toggleLikeMutation.mutate({ letterId: id, userId: user.id });
  };

  const isLiked = (id: string) => likedIds?.has(id) ?? false;

  const writerCount = useMemo(() => {
    if (!letters) return 0;
    return new Set(letters.map((l) => l.sender_id).filter(Boolean)).size;
  }, [letters]);

  const heartCount = useMemo(() => {
    if (!letters) return 0;
    return letters.reduce((sum, l) => sum + (l.like_count ?? 0), 0);
  }, [letters]);

  return (
    <div className="space-y-4">
      {/* Stats strip */}
      <div className="flex gap-3">
        {[
          { label: t('plaza.stats.letters'), value: String(letters?.length ?? 0), icon: PenLine },
          { label: t('plaza.stats.writers'), value: String(writerCount), icon: Feather },
          { label: t('plaza.stats.hearts'), value: String(heartCount), icon: Heart },
        ].map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.label} className="letter-card flex flex-1 items-center gap-2.5 rounded-xl px-4 py-3">
              <Icon className="h-4 w-4 shrink-0 text-[hsl(var(--opp-accent))]" />
              <div>
                <p className="text-sm font-bold text-[hsl(var(--opp-ink))]">{stat.value}</p>
                <p className="text-caption text-[hsl(var(--opp-accent-strong))]">{stat.label}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 overflow-x-auto pb-0.5">
        {FILTERS.map((f) => (
          <OppButton
            key={f.id}
            onClick={() => setFilter(f.id)}
            aria-pressed={filter === f.id}
            className={`min-h-11 shrink-0 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all ${
              filter === f.id
                ? 'bg-primary text-primary-foreground'
                : 'bg-[hsl(var(--opp-accent-soft))] text-[hsl(var(--opp-accent-strong))] hover:bg-[hsl(var(--opp-hero-decoration))]'
            }`}
          >
            {f.label}
          </OppButton>
        ))}
      </div>

      {/* Letter wall */}
      {isLoading ? (
        <div className="space-y-4">
          <LetterCardSkeleton />
          <LetterCardSkeleton />
        </div>
      ) : filteredLetters.length > 0 ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {filteredLetters.map((letter) => (
            <div
              key={letter.id}
              className="letter-card overflow-hidden rounded-2xl cursor-pointer transition-all hover:shadow-lg hover:-translate-y-0.5"
            >
              <div className="p-5">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-serif text-base font-bold text-[hsl(var(--opp-ink))] leading-snug">
                    {letter.title || t('plaza.untitled')}
                  </h3>
                  <span className="shrink-0 rounded-full bg-[hsl(var(--opp-accent-soft))]/80 px-2 py-0.5 text-caption font-semibold text-[hsl(var(--opp-accent-strong))]">
                    {new Date(letter.created_at).toLocaleDateString()}
                  </span>
                </div>
                <p className="mt-2.5 text-sm leading-relaxed text-[hsl(var(--opp-ink))] line-clamp-3">
                  {letter.content}
                </p>
                <div className="mt-4 flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-[hsl(var(--opp-hero-decoration))] text-xs font-bold text-[hsl(var(--opp-ink))]">
                      A
                    </div>
                    {letter.is_anonymous ? (
                      <span className="text-xs font-medium text-[hsl(var(--opp-accent))]">{t('write.anonymous.sender')}</span>
                    ) : (
                      <span className="text-xs font-medium text-[hsl(var(--opp-accent-strong))]">{letter.author_name ?? t('museum.anonymous')}</span>
                    )}
                  </div>
                  <OppButton
                    onClick={() => toggleLike(letter.id)}
                    aria-label={isLiked(letter.id) ? 'Unlike' : 'Like'}
                    aria-pressed={isLiked(letter.id)}
                    className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold transition-all ${
                      isLiked(letter.id)
                        ? 'bg-[hsl(var(--opp-danger-bg))] text-[hsl(var(--opp-danger-fg))]'
                        : 'bg-[hsl(var(--opp-accent-soft))] text-[hsl(var(--opp-accent-strong))] hover:bg-[hsl(var(--opp-hero-decoration))]'
                    }`}
                  >
                    <Heart className="h-3 w-3" fill={isLiked(letter.id) ? 'currentColor' : 'none'} />
                    {letter.like_count ?? 0}
                  </OppButton>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <SemanticState compact illustration="empty-public-wall" kind="empty" title={t('plaza.empty')} description={t('discover.subtitle')} />
      )}
    </div>
  );
}

// ── Museum Tab (from Museum.tsx) ──
function MuseumTab() {
  const { t } = useCopy();
  const { user } = useSharedAuth();
  const [theme, setTheme] = useState('all');
  const [sort, setSort] = useState('newest');
  const [page, setPage] = useState(1);
  const [showSort, setShowSort] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [selectedExhibitionId, setSelectedExhibitionId] = useState<string | null>(null);
  const [collectionState, setCollectionState] = useState<Record<string, boolean>>({});
  const sortRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showSort) return;
    const handleClick = (e: MouseEvent) => {
      if (sortRef.current && !sortRef.current.contains(e.target as Node)) {
        setShowSort(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showSort]);

  const { data: myCollections } = useMyCollections();
  const toggleCollection = useToggleCollection();
  const collectedIds = new Set(myCollections?.map((c: { item_id: string }) => c.item_id) ?? []);

  const isCollected = (itemId: string) => {
    if (itemId in collectionState) return collectionState[itemId];
    return collectedIds.has(itemId);
  };

  const handleToggleCollection = (e: React.MouseEvent, itemId: string) => {
    e.stopPropagation();
    if (!user) return;
    const next = !isCollected(itemId);
    setCollectionState((prev) => ({ ...prev, [itemId]: next }));
    toggleCollection.mutate({ itemId, userId: user.id }, {
      onError: () => setCollectionState((prev) => ({ ...prev, [itemId]: !next })),
    });
  };

  const THEMES = [
    { id: 'all', label: t('museum.filter.all') },
    { id: 'future', label: t('museum.filter.future') },
    { id: 'memory', label: t('museum.filter.memory') },
    { id: 'warmth', label: t('museum.filter.warmth') },
    { id: 'story', label: t('museum.filter.story') },
    { id: 'friendship', label: t('museum.filter.friendship') },
  ];

  const SORT_OPTIONS = [
    { id: 'newest', label: t('museum.sort.newest') },
    { id: 'views', label: t('museum.sort.views') },
    { id: 'likes', label: t('museum.sort.likes') },
  ];

  const PAGE_SIZE = 12;
  const { data: museumData, isLoading } = useMuseumItems({ theme, sort, page, pageSize: PAGE_SIZE });
  const { data: exhibitions } = useMuseumExhibitions();
  const { data: exhibitionEntries } = useExhibitionEntries(selectedExhibitionId ?? undefined);

  const pageItems = museumData?.items ?? [];
  const total = museumData?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-5">
      {/* Exhibitions carousel */}
      {exhibitions && exhibitions.length > 0 && (
        <section>
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-serif text-lg font-bold text-[hsl(var(--opp-ink))]">{t('museum.exhibitions')}</h3>
          </div>
          <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-none">
            {exhibitions.map((ex) => (
              <OppButton
                key={ex.id}
                type="button"
                aria-label={ex.title}
                aria-pressed={selectedExhibitionId === ex.id}
                onClick={() => setSelectedExhibitionId(selectedExhibitionId === ex.id ? null : ex.id)}
                className={`letter-card relative min-w-[var(--litopia-layout-220px)] max-w-[var(--litopia-layout-220px)] shrink-0 overflow-hidden rounded-xl text-left cursor-pointer hover:shadow-lg transition-all hover:-translate-y-0.5 ${
                  selectedExhibitionId === ex.id ? 'ring-2 ring-[hsl(var(--ring))]' : ''
                }`}
              >
                <div className="h-24 bg-[hsl(var(--opp-accent-soft))] flex items-center justify-center">
                  <BookOpen className="h-8 w-8 text-[hsl(var(--opp-accent))]" />
                </div>
                {ex.is_featured && (
                  <div className="absolute right-2 top-2 flex items-center gap-1 rounded-full bg-primary px-2 py-0.5 text-caption font-bold text-primary-foreground">
                    <Star className="h-2.5 w-2.5" /> {t('museum.exhibition.featured')}
                  </div>
                )}
                <div className="p-3">
                  <h4 className="truncate font-serif text-sm font-bold text-[hsl(var(--opp-ink))]">{ex.title}</h4>
                  <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-[hsl(var(--opp-accent-strong))]">{ex.description}</p>
                  <div className="mt-2 flex items-center gap-2 text-xs text-[hsl(var(--opp-accent))]">
                    <Eye className="h-3 w-3" />
                    <span>{ex.view_count ?? 0} {t('museum.exhibition.views')}</span>
                  </div>
                </div>
              </OppButton>
            ))}
          </div>
        </section>
      )}

      {/* Exhibition entries panel */}
      {selectedExhibitionId && (
        <section className="letter-card rounded-2xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-serif text-base font-bold text-[hsl(var(--opp-ink))]">
              {exhibitions?.find((e) => e.id === selectedExhibitionId)?.title ?? t('museum.exhibition.content')}
            </h3>
            <OppButton aria-label="关闭展览内容" onClick={() => setSelectedExhibitionId(null)} className="min-h-11 min-w-11 rounded-full p-2 text-[hsl(var(--opp-muted))] hover:bg-[hsl(var(--opp-accent-soft))] hover:text-[hsl(var(--opp-accent-strong))] transition-colors">
              <X className="h-4 w-4" />
            </OppButton>
          </div>
          {exhibitionEntries && exhibitionEntries.length > 0 ? (
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
              {exhibitionEntries.map((entry: { id: string; opp_museum_items: { id: string; title: string; image_url?: string; view_count?: number; like_count?: number; category?: string } | null }) => {
                const item = entry.opp_museum_items;
                if (!item) return null;
                return (
                  <div key={entry.id} className="letter-card overflow-hidden rounded-xl">
                    {item.image_url ? (
                      <img src={item.image_url} alt={item.title} className="h-28 w-full object-cover" />
                    ) : (
                      <div className="h-28 w-full bg-[hsl(var(--opp-hero))] flex items-center justify-center">
                        <BookOpen className="h-7 w-7 text-[hsl(var(--opp-muted))]" />
                      </div>
                    )}
                    <div className="p-2.5">
                      <h4 className="truncate font-serif text-xs font-bold text-[hsl(var(--opp-ink))]">{item.title}</h4>
                      <div className="mt-1.5 flex items-center gap-2 text-caption text-[hsl(var(--opp-accent))]">
                        <span className="flex items-center gap-0.5"><Eye className="h-2.5 w-2.5" />{item.view_count ?? 0}</span>
                        <span className="flex items-center gap-0.5"><Heart className="h-2.5 w-2.5" />{item.like_count ?? 0}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <SemanticState compact illustration="empty-exhibition" kind="empty" title={t('museum.exhibition.empty')} />
          )}
        </section>
      )}

      {/* Filter + Sort */}
      <div className="flex items-center gap-3">
        <div className="flex flex-1 gap-2 overflow-x-auto pb-0.5 scrollbar-none">
          {THEMES.map((t) => (
            <OppButton
              key={t.id}
              onClick={() => { setTheme(t.id); setPage(1); }}
              className={`shrink-0 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all ${
                theme === t.id ? 'bg-primary text-primary-foreground shadow-sm' : 'bg-[hsl(var(--opp-accent-soft))] text-[hsl(var(--opp-accent-strong))] hover:bg-[hsl(var(--opp-hero-decoration))]'
              }`}
            >
              {t.label}
            </OppButton>
          ))}
        </div>
        <div ref={sortRef} className="relative shrink-0">
          <OppButton
            onClick={() => setShowSort(!showSort)}
            aria-expanded={showSort}
            aria-haspopup="listbox"
            className="flex min-h-11 items-center gap-1.5 rounded-full border border-[hsl(var(--opp-border))] bg-[hsl(var(--opp-surface))] px-3 py-1.5 text-xs font-semibold text-[hsl(var(--opp-accent-strong))] hover:border-[hsl(var(--opp-accent))] transition-colors"
          >
            <SlidersHorizontal className="h-3.5 w-3.5" />
            {SORT_OPTIONS.find((o) => o.id === sort)?.label}
          </OppButton>
          {showSort && (
            <div className="absolute right-0 top-full z-10 mt-1 w-36 overflow-hidden rounded-xl border border-[hsl(var(--opp-border))] bg-[hsl(var(--opp-surface))] shadow-lg">
              {SORT_OPTIONS.map((o) => (
                <OppButton key={o.id} onClick={() => { setSort(o.id); setPage(1); setShowSort(false); }}
                  className={`min-h-11 w-full px-3 py-2 text-left text-xs font-medium transition-colors hover:bg-[hsl(var(--opp-hero))] ${sort === o.id ? 'text-[hsl(var(--opp-accent-strong))] font-semibold bg-[hsl(var(--opp-hero))]' : 'text-[hsl(var(--opp-accent-strong))]'}`}>
                  {o.label}
                </OppButton>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Items grid */}
      {isLoading ? (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => <GridItemSkeleton key={i} />)}
        </div>
      ) : pageItems.length > 0 ? (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            {pageItems.map((item) => (
              <OppButton key={item.id} onClick={() => setSelectedItemId(selectedItemId === item.id ? null : item.id)}
                className="letter-card relative overflow-hidden rounded-xl cursor-pointer transition-all hover:shadow-lg hover:-translate-y-0.5">
                {user && (
                  <OppButton
                    onClick={(e) => handleToggleCollection(e, item.id)}
                    aria-label={isCollected(item.id) ? t('museum.collection.remove') : t('museum.collection.add')}
                    className={`absolute right-2 top-2 z-10 flex min-h-11 min-w-11 items-center justify-center rounded-full shadow-sm transition-all ${
                      isCollected(item.id) ? 'bg-primary text-primary-foreground' : 'bg-[hsl(var(--opp-surface)/0.8)] text-[hsl(var(--opp-muted))] hover:bg-[hsl(var(--opp-surface))] hover:text-[hsl(var(--opp-accent-strong))]'
                    }`}>
                    <Bookmark className="h-3.5 w-3.5" fill={isCollected(item.id) ? 'currentColor' : 'none'} />
                  </OppButton>
                )}
                {item.image_url ? (
                  <img src={item.image_url} alt={item.title} className="h-36 w-full object-cover" />
                ) : (
                  <div className="h-36 w-full bg-[hsl(var(--opp-hero))] flex items-center justify-center">
                    <BookOpen className="h-8 w-8 text-[hsl(var(--opp-muted))]" />
                  </div>
                )}
                <div className="p-3">
                  <h4 className="truncate font-serif text-sm font-bold text-[hsl(var(--opp-ink))]">{item.title}</h4>
                  {item.category && (
                    <span className="mt-1.5 inline-block rounded-full bg-[hsl(var(--opp-accent-soft))] px-2 py-0.5 text-caption font-semibold text-[hsl(var(--opp-accent-strong))]">{item.category}</span>
                  )}
                  <div className="mt-2 flex items-center gap-3 text-xs text-[hsl(var(--opp-accent))]">
                    <span className="flex items-center gap-1"><Eye className="h-3 w-3" />{item.view_count ?? 0}</span>
                    <span className="flex items-center gap-1"><Heart className="h-3 w-3" />{item.like_count ?? 0}</span>
                  </div>
                </div>
              </OppButton>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 pt-2">
              <OppButton onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                className="flex min-h-11 items-center gap-1.5 rounded-full border border-[hsl(var(--opp-border))] px-4 py-2 text-sm font-medium text-[hsl(var(--opp-accent-strong))] hover:bg-[hsl(var(--opp-hero))] disabled:opacity-40 transition-colors">
                <ChevronLeft className="h-4 w-4" /> {t('museum.prev')}
              </OppButton>
              <span className="text-sm text-[hsl(var(--opp-accent-strong))]">{page} / {totalPages}</span>
              <OppButton onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="flex min-h-11 items-center gap-1.5 rounded-full border border-[hsl(var(--opp-border))] px-4 py-2 text-sm font-medium text-[hsl(var(--opp-accent-strong))] hover:bg-[hsl(var(--opp-hero))] disabled:opacity-40 transition-colors">
                {t('museum.next')} <ChevronRight className="h-4 w-4" />
              </OppButton>
            </div>
          )}

          {/* Item comment panel */}
          {selectedItemId && (() => {
            const item = pageItems.find((i) => i.id === selectedItemId);
            if (!item) return null;
            return (
              <div className="letter-card rounded-2xl p-5 space-y-4">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-serif text-base font-bold text-[hsl(var(--opp-ink))]">{item.title}</h3>
                  <OppButton aria-label="关闭藏品详情" onClick={() => setSelectedItemId(null)} className="min-h-11 min-w-11 shrink-0 rounded-full p-2 text-[hsl(var(--opp-muted))] hover:bg-[hsl(var(--opp-accent-soft))] hover:text-[hsl(var(--opp-accent-strong))] transition-colors">
                    <X className="h-4 w-4" />
                  </OppButton>
                </div>
                <CommentSection targetId={selectedItemId} targetType="museum_item" />
              </div>
            );
          })()}
        </>
      ) : (
        <SemanticState illustration="empty-museum" kind="empty" title={t('museum.empty')} description={t('discover.subtitle')} />
      )}
    </div>
  );
}

// ── Drift Tab ──
function DriftTab() {
  const { t } = useCopy();
  return (
    <div className="space-y-4">
      <div className="letter-card flex flex-col items-center p-8 text-center">
        <Droplets className="h-12 w-12 text-[hsl(var(--opp-muted))]" />
        <h3 className="mt-3 font-serif text-lg font-bold text-[hsl(var(--opp-ink))]">{t('discover.drift.title')}</h3>
        <p className="mt-1 text-sm text-[hsl(var(--opp-accent-strong))]">{t('discover.drift.desc')}</p>
        <Link
          to="/drift"
          className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground shadow hover:bg-[hsl(var(--opp-accent-hover))] transition-colors"
        >
          <Droplets className="h-4 w-4" />
          {t('discover.drift.enter')}
        </Link>
      </div>
    </div>
  );
}
